import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	ColorPicker,
	isReadable,
	useAutoTransitioningColors,
	useBuiltInColorMap,
} from '@atlassian/color-picker';
import {
	CommonLayout,
	ImageField,
	UnsplashSearchField,
	type UnsplashSearchFieldProps,
	useProductDependencies,
} from '@atlassian/company-hub-common';
import {
	resolveAdfColorForPicker,
	toAdfColor,
} from '@atlassian/company-hub-common/hub-brand-palette';
import { useHubTheming } from '@atlassian/company-hub-common/hub-theming-context';

import { carouselConfigAnalyticSource } from '../../../../../constants';
import { frameNewParametersV2 } from '../../../../../controllers/use-carousel-extension-parameters-and-actions';
import { type UpdateFrameFormFieldType } from '../../../../../controllers/use-carousel-extension-parameters-and-actions/types';
import { FRAME_FIELDS } from '../../../../../types';
import { CarouselLayoutOptions } from '../../../fields/layout-options';

import { FrameField, type FrameFieldProps } from './frame-field';
import { messages } from './messages';

export type FrameContentProps = {
	showBgAndTextColorOptions?: boolean;
	showLayout?: boolean;
	showMediaPicker?: boolean;
	updateFrameFormField: UpdateFrameFormFieldType;
} & Pick<FrameFieldProps, 'frameParameters' | 'isDisabled'> &
	Pick<UnsplashSearchFieldProps, 'contentId' | 'initialUnsplashResults'>;

const styles = cssMap({
	wrapper: {
		alignSelf: 'stretch',
		flexDirection: 'column',
		marginTop: token('space.100'),
		marginBottom: token('space.100'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
	bgAndTextColorOptionsWrapper: {
		marginTop: token('space.200'),
	},
});

export const FrameContent = ({
	contentId,
	frameParameters,
	initialUnsplashResults,
	isDisabled,
	showBgAndTextColorOptions,
	updateFrameFormField,
	showMediaPicker,
	showLayout,
}: FrameContentProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const {
		cardId,
		imageSrc,
		imageAltText,
		confluencePageId,
		backgroundColor = frameNewParametersV2.backgroundColor,
		textColor = frameNewParametersV2.textColor,
		layout,
	} = frameParameters;
	const { isCompanyHub, enableLinkInputInMediaPicker, isSSRProductInDarkMode, branding } =
		useProductDependencies();
	const { hideDefaultColorPalette, brandBackgroundColorMap } = branding ?? {};
	const { backgroundColorMap, textColorMap, emptyColorMap } =
		useBuiltInColorMap(isSSRProductInDarkMode);

	const theming = useHubTheming();
	const isV2 = !!theming?.isAdvancedThemingEnabled;
	const palette = isV2 ? theming.palette : undefined;
	const primaryColor = isV2 ? theming.primaryColor : undefined;
	const v2BrandMap = isV2 ? theming.brandBackgroundColorMap : undefined;

	// Resolve to literal hex so isReadable in useAutoTransitioningColors works
	// against the actual colour values, not against CSS var refs (which would
	// silently fail).
	const resolvedBackgroundColor =
		isV2 && palette && primaryColor
			? resolveAdfColorForPicker(backgroundColor, palette, 'primary.200', primaryColor)
			: backgroundColor;
	const resolvedTextColor =
		isV2 && palette && primaryColor
			? resolveAdfColorForPicker(
					textColor,
					palette,
					'primary.1000',
					isReadable('#FFFFFF', primaryColor) ? '#FFFFFF' : '#000000',
				)
			: textColor;

	const isReadableBackgroundColor = (color: string) => isReadable(color, resolvedTextColor);
	const isReadableTextColor = (color: string) => isReadable(color, resolvedBackgroundColor);

	const updateBackgroundColor = (bgColor: string) => {
		const next = isV2 && palette ? toAdfColor(bgColor, palette) : bgColor;
		updateFrameFormField(cardId, {
			[FRAME_FIELDS.BACKGROUND_COLOR]: next,
		});
	};
	const updateTextColor = (color: string) => {
		const next = isV2 && palette ? toAdfColor(color, palette) : color;
		updateFrameFormField(cardId, {
			[FRAME_FIELDS.TEXT_COLOR]: next,
		});
	};

	const canShowImagePicker = layout !== CommonLayout.NO_IMAGE_BACKGROUND;

	useAutoTransitioningColors(
		resolvedBackgroundColor,
		isReadableTextColor,
		resolvedTextColor,
		updateTextColor,
		branding?.defaultParameters,
	);

	return (
		<Flex xcss={styles.wrapper}>
			{showLayout && (
				<CarouselLayoutOptions
					frameParameters={frameParameters}
					updateFrameFormField={updateFrameFormField}
					contentId={contentId}
				/>
			)}
			<FrameField
				frameParameters={frameParameters}
				isDisabled={isDisabled}
				updateFrameFormField={updateFrameFormField}
			/>
			{canShowImagePicker &&
				(showMediaPicker ? (
					<ImageField
						shouldAllowLinkFromUrl={enableLinkInputInMediaPicker}
						analyticsSource={carouselConfigAnalyticSource}
						itemId={cardId}
						contentId={contentId}
						imageSrc={imageSrc}
						imageAltText={imageAltText}
						updateItemFormField={updateFrameFormField}
					/>
				) : (
					<UnsplashSearchField
						analyticsSource={carouselConfigAnalyticSource}
						idPrefix="frame"
						itemId={cardId}
						contentId={contentId}
						confluencePageId={confluencePageId}
						imageSrc={imageSrc}
						imageAltText={imageAltText}
						initialUnsplashResults={initialUnsplashResults}
						isNoImageAllowed={false}
						updateItemFormField={updateFrameFormField}
					/>
				))}
			{showBgAndTextColorOptions && (
				<Stack space="space.200" xcss={styles.bgAndTextColorOptionsWrapper}>
					<Inline alignBlock="center" spread="space-between">
						<Text color="color.text">
							<FormattedMessage {...messages.backgroundColor} />
						</Text>
						<ColorPicker
							builtInColorMap={
								v2BrandMap
									? v2BrandMap
									: hideDefaultColorPalette && brandBackgroundColorMap
										? brandBackgroundColorMap
										: backgroundColorMap
							}
							isReadableColor={isReadableBackgroundColor}
							onChange={updateBackgroundColor}
							value={resolvedBackgroundColor}
							pickerTitle={messages.backgroundColor}
							validationSuccessMessage={formatMessage(messages.colorValidationSuccessMessage)}
							validationFailMessage={formatMessage(messages.colorValidationFailMessage)}
							showCustomPalette={isCompanyHub}
							analyticsSource={carouselConfigAnalyticSource}
						/>
					</Inline>
					<Inline alignBlock="center" spread="space-between">
						<Text color="color.text">
							<FormattedMessage {...messages.textColor} />
						</Text>
						<ColorPicker
							builtInColorMap={
								v2BrandMap ? v2BrandMap : hideDefaultColorPalette ? emptyColorMap : textColorMap
							}
							isReadableColor={isReadableTextColor}
							onChange={updateTextColor}
							value={resolvedTextColor}
							pickerTitle={messages.textColor}
							validationSuccessMessage={formatMessage(messages.colorValidationSuccessMessage)}
							validationFailMessage={formatMessage(messages.colorValidationFailMessage)}
							showCustomPalette={isCompanyHub}
							analyticsSource={carouselConfigAnalyticSource}
							showTextIcon
						/>
					</Inline>
				</Stack>
			)}
		</Flex>
	);
};
